import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import { calculateInvoiceTotals } from '../utils/helpers';
import Alert from '../components/Alert';
import InvoiceItemsTable from '../components/InvoiceItemsTable';
import Layout from '../components/Layout';
import CustomDropdown from '../components/CustomDropdown';
import { 
  Pencil, 
  ArrowLeft, 
  ShieldAlert, 
  Info, 
  Package, 
  Calculator, 
  FileText, 
  Loader2,
  FileSearch,
  Save
} from 'lucide-react';
import './CreateInvoice.css';
import './CreateQuotation.css';

const EditQuotation = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [products, setProducts] = useState([]);
    const [quotation, setQuotation] = useState(null);

    const [formData, setFormData] = useState({
        items: [],
        discount: 0,
        discountType: 'amount',
        taxType: 'excluded',
        validUntil: '',
        notes: '',
        termsAndConditions: ''
    });

    useEffect(() => {
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [quotationRes, settingsRes, productsRes] = await Promise.all([
                api.get(`/quotations/${id}`),
                api.get('/settings'),
                api.get('/products?limit=1000')
            ]);

            const quotationData = quotationRes.data.quotation;
            setQuotation(quotationData);
            
            setProducts(productsRes.data.products);

            if (quotationData.status === 'converted') {
                setError('This quotation has been converted to an invoice and cannot be edited');
            }

            // Map items and try to match with products
            const mappedItems = quotationData.items.map(item => {
                const matchingProduct = productsRes.data.products.find(
                    p => p.description === item.description || p.hsnSac === item.hsnSac
                );

                return {
                    ...item,
                    product: matchingProduct ? matchingProduct._id : ''
                };
            });

            setFormData({
                items: mappedItems,
                discount: quotationData.discount || 0,
                discountType: quotationData.discountType || 'amount',
                taxType: quotationData.taxType || 'excluded',
                validUntil: quotationData.validUntil ? new Date(quotationData.validUntil).toISOString().split('T')[0] : '',
                notes: quotationData.notes || '',
                termsAndConditions: quotationData.termsAndConditions || ''
            });
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load quotation');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (formData.items.length === 0) {
            setError('At least one item is required');
            return;
        }

        const hasEmptyItems = formData.items.some(
            item => (!item.productName && !item.description) || !item.hsnSac || Number(item.quantity) <= 0 || Number(item.rate) < 0
        );

        if (hasEmptyItems) {
            setError('Please fill all item details (product name/description, HSN/SAC, quantity, and rate)');
            return;
        }

        try {
            setLoading(true);

            const sanitizedData = {
                ...formData,
                items: formData.items.map(item => {
                    const sanitizedItem = { ...item };
                    sanitizedItem.description = (sanitizedItem.description || sanitizedItem.productName || '').trim();
                    sanitizedItem.productName = (sanitizedItem.productName || '').trim();
                    sanitizedItem.unit = (sanitizedItem.unit || 'pcs').trim();
                    if (!sanitizedItem.product) {
                        delete sanitizedItem.product;
                    }
                    return sanitizedItem;
                })
            };

            await api.put(`/quotations/${id}`, sanitizedData);
            setSuccess('Quotation updated successfully!');

            setTimeout(() => {
                navigate(`/quotations/${id}`);
            }, 1500);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update quotation');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div style={{ padding: '40px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
                    <Loader2 size={40} className="animate-spin" style={{ color: '#06b6d4' }} />
                    <p>Loading Quotation...</p>
                </div>
            </Layout>
        );
    }

    if (!quotation) {
        return (
            <Layout>
                <div className="page-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <FileSearch size={28} style={{ color: '#ef4444' }} />
                        <h1>Quotation Not Found</h1>
                    </div>
                    <button className="btn btn-secondary" onClick={() => navigate('/quotations')} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <ArrowLeft size={18} /> Back to Quotations
                    </button>
                </div>
            </Layout>
        );
    }

    const isIGST = quotation.isIGST;
    const totals = calculateInvoiceTotals(formData.items, isIGST, formData.discount, formData.discountType, formData.taxType);
    const canEdit = quotation.status !== 'converted';

    return (
        <div>
            <div className="create-invoice-container">
                <div className="page-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Pencil size={28} style={{ color: '#06b6d4' }} />
                        <h1>Edit Quotation - {quotation.quotationNumber}</h1>
                    </div>
                    <button className="btn btn-secondary" onClick={() => navigate(`/quotations/${id}`)} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <ArrowLeft size={18} /> Back to Quotation
                    </button>
                </div>

                {error && <Alert type="error" message={error} onClose={() => setError('')} />}
                {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

                {!canEdit ? (
                    <div className="card">
                        <div style={{ padding: '40px', textAlign: 'center', color: '#e74c3c', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
                            <ShieldAlert size={48} />
                            <div>
                                <h3 style={{ margin: '0 0 10px 0' }}>This quotation cannot be edited</h3>
                                <p style={{ margin: 0 }}>The quotation has been converted to an invoice</p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                         <div className="card card-customer" style={{ overflow: 'visible', position: 'relative', zIndex: 10 }}>
                            <div className="card-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Info size={18} /> Quotation Details (Read-Only)
                                </div>
                            </div>
                            <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                                <div><strong>Quotation Number:</strong> {quotation.quotationNumber}</div>
                                <div><strong>Quotation Date:</strong> {new Date(quotation.quotationDate).toLocaleDateString()}</div>
                                <div><strong>Customer:</strong> {quotation.customerDetails.name} - {quotation.customerDetails.phone}</div>
                                <div><strong>Place of Supply:</strong> {quotation.customerDetails.placeOfSupply}</div>
                                <div><strong>Tax Configuration:</strong> {isIGST ? 'IGST' : 'CGST + SGST'}</div>
                            </div>

                            <div style={{ padding: '25px', paddingTop: '18px', display: 'flex', gap: '20px' }}>
                                <div className="form-group" style={{ flex: 1 }}>
                                    <label className="form-label">Valid Until <span style={{ color: '#ef4444' }}>*</span></label>
                                    <input
                                        type="date"
                                        name="validUntil"
                                        className="form-control"
                                        value={formData.validUntil}
                                        onChange={handleChange}
                                        required
                                        min={quotation.quotationDate}
                                    />
                                </div>
                                <div className="form-group" style={{ flex: 1 }}>
                                    <label className="form-label">Tax Calculation Mode</label>
                                    <select
                                        name="taxType"
                                        value={formData.taxType}
                                        onChange={handleChange}
                                        className="form-control"
                                    >
                                        <option value="excluded">Tax Excluded</option>
                                        <option value="included">Tax Included</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="card card-items" style={{ overflow: 'visible', position: 'relative', zIndex: 9 }}>
                            <div className="card-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Package size={18} /> Quotation Items
                                </div>
                            </div>
                            <InvoiceItemsTable
                                items={formData.items}
                                onItemsChange={(items) => setFormData(prev => ({ ...prev, items }))}
                                products={products}
                            />
                        </div>

                        <div className="card card-totals" style={{ overflow: 'visible', position: 'relative', zIndex: 8 }}>
                            <div className="card-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Calculator size={18} /> Quotation Totals
                                </div>
                            </div>
                            <table style={{ width: '100%', maxWidth: '400px', marginLeft: 'auto' }}>
                                <tbody>
                                    <tr>
                                        <td><strong>Subtotal:</strong></td>
                                        <td style={{ textAlign: 'right' }}>₹{totals.subtotal.toFixed(2)}</td>
                                    </tr>
                                    {formData.discount > 0 && (
                                        <tr>
                                            <td><strong>Discount ({formData.discountType === 'percentage' ? `${formData.discount}%` : '₹'}):</strong></td>
                                            <td style={{ textAlign: 'right', color: '#e74c3c' }}>-₹{totals.discountAmount.toFixed(2)}</td>
                                        </tr>
                                    )}
                                    {isIGST ? (
                                        <tr>
                                            <td><strong>IGST:</strong></td>
                                            <td style={{ textAlign: 'right' }}>₹{totals.igst.toFixed(2)}</td>
                                        </tr>
                                    ) : (
                                        <>
                                            <tr>
                                                <td><strong>CGST:</strong></td>
                                                <td style={{ textAlign: 'right' }}>₹{totals.cgst.toFixed(2)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>SGST:</strong></td>
                                                <td style={{ textAlign: 'right' }}>₹{totals.sgst.toFixed(2)}</td>
                                            </tr>
                                        </>
                                    )}
                                    <tr>
                                        <td><strong>Total Tax:</strong></td>
                                        <td style={{ textAlign: 'right' }}>₹{totals.totalTax.toFixed(2)}</td>
                                    </tr>
                                    {totals.roundOff !== 0 && (
                                        <tr>
                                            <td><strong>Round Off:</strong></td>
                                            <td style={{ textAlign: 'right' }}>₹{totals.roundOff.toFixed(2)}</td>
                                        </tr>
                                    )}
                                    <tr style={{ borderTop: '2px solid #333', fontSize: '18px' }}>
                                        <td><strong>Grand Total:</strong></td>
                                        <td style={{ textAlign: 'right' }}><strong>₹{totals.grandTotal.toFixed(2)}</strong></td>
                                    </tr>
                                </tbody>
                            </table>

                            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #ddd' }}>
                                <h4>Apply Discount</h4>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Discount Type</label>
                                        <CustomDropdown
                                            name="discountType"
                                            value={formData.discountType}
                                            onChange={handleChange}
                                            options={[
                                                { value: 'amount', label: '₹ Amount' },
                                                { value: 'percentage', label: '% Percent' }
                                            ]}
                                            className="form-control"
                                            style={{ height: '42px', minWidth: '130px' }}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Discount Value</label>
                                        <input
                                            type="number"
                                            name="discount"
                                            className="form-control"
                                            value={formData.discount}
                                            onChange={handleChange}
                                            min="0"
                                            step="0.01"
                                            placeholder="0.00"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card card-additional" style={{ overflow: 'visible', position: 'relative', zIndex: 7 }}>
                            <div className="card-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FileText size={18} /> Additional Information
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Notes</label>
                                <textarea
                                    name="notes"
                                    className="form-control"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    rows="3"
                                    placeholder="Any additional notes for this quotation"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Terms & Conditions</label>
                                <textarea
                                    name="termsAndConditions"
                                    className="form-control"
                                    value={formData.termsAndConditions}
                                    onChange={handleChange}
                                    rows="4"
                                />
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '20px' }}>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => navigate(`/quotations/${id}`)}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={loading}
                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                            >
                                {loading ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                                {loading ? 'Updating...' : 'Update Quotation'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default EditQuotation;
